// A preferência de tema do console (#25).
//
// TRÊS estados, não dois: `auto` segue o sistema e muda junto com ele, em tempo real. Um seletor de
// dois estados obriga quem usa a reescolher toda vez que o sistema troca sozinho ao anoitecer.
//
// Quem resolve `auto` é este módulo, e o resultado vai para `data-theme` no <html>. É por isso que o
// CSS usa `html[data-theme=...]` em vez de `prefers-color-scheme`: uma media query sozinha não
// distingue "quero claro" de "o sistema está claro", e essas duas coisas têm consequências
// diferentes no dia em que o sistema muda.

export type Preferencia = "auto" | "light" | "dark";
export type Tema = "light" | "dark";

export const CHAVE = "@hub:tema";
const CONSULTA = "(prefers-color-scheme: dark)";

export function ehPreferencia(v: unknown): v is Preferencia {
  return v === "auto" || v === "light" || v === "dark";
}

// Sempre devolve algo utilizável. Navegador em janela privada recusa o localStorage com exceção, e
// uma tela que não abre por causa disso seria um preço absurdo por uma preferência de cor.
export function lerPreferencia(): Preferencia {
  try {
    const guardado = localStorage.getItem(CHAVE);
    if (ehPreferencia(guardado)) return guardado;
  } catch {
    // Sem armazenamento: `auto` é o padrão e nada quebra.
  }
  return "auto";
}

export function guardarPreferencia(p: Preferencia): void {
  try {
    localStorage.setItem(CHAVE, p);
  } catch {
    // A escolha vale para esta aba e acabou. Melhor isso do que a tela cair ao clicar.
  }
}

export function resolver(p: Preferencia, sistemaEscuro: boolean): Tema {
  if (p === "auto") return sistemaEscuro ? "dark" : "light";
  return p;
}

export function sistemaEscuro(): boolean {
  return typeof window !== "undefined" && window.matchMedia(CONSULTA).matches;
}

export function aplicar(tema: Tema): void {
  document.documentElement.dataset.theme = tema;
}

// Avisa quando o SISTEMA muda, para quem está em `auto` acompanhar sem recarregar. Devolve como
// desinscrever — sem isso, cada remontagem deixaria um ouvinte vivo.
export function observarSistema(
  aoMudar: (escuro: boolean) => void,
): () => void {
  const mq = window.matchMedia(CONSULTA);
  const ouvinte = (e: MediaQueryListEvent) => aoMudar(e.matches);
  mq.addEventListener("change", ouvinte);
  return () => mq.removeEventListener("change", ouvinte);
}
